import ButtonsBlock from '@/components/common/buttons-block/buttons-block';
import DialogTemplate from '@/components/common/dialog-template/dialog-template';
import FormInputsSeparator from '@/components/common/form-inputs-separator/form-inputs-separator';
import useGetAvatar from '@/hooks/useGetAvatar';
import useGetCurrentUserV2 from '@/hooks/useGetCurrentUser';
import useGetGroupAvatars from '@/hooks/useGetGroupAvatars';
import useGetMetadataAvatarTypes from '@/hooks/useGetMetadataAvatarTypes';
import { composeUri } from '@/utils/compose';
import {
    AVATARS,
    FINGER_ID,
    FINGERS,
    GROUP_AVATAR_SITE_PREFIX,
    INCIDENT_SERVICE_PLACE,
    PLACES,
    PLACES_METADATA_AVATAR_TYPE_NAME,
    ROLE_BIG_DEMO,
} from '@/utils/CONST';
import useGetAlphaId from '@/utils/useGetAlphaId';
import { AddHomeOutlined } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { composeUriId } from 'base-tools-360smartconnect/utils/compose';
import { isNil, omitBy } from 'lodash';
import { BooleanInput, Form, required, TextInput, useCreate, useNotify, usePermissions, useTranslate } from 'react-admin';

interface CreatePlaceDialogProps {
    open: boolean;
    onClose: () => void;
    siteId: string;
}

const CreatePlaceDialog = ({ open, onClose, siteId }: CreatePlaceDialogProps) => {
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const [create, { isLoading: isLoadingCreate }] = useCreate();
    const notify = useNotify();
    const { isLoadingGetAlphaId, getAlphaId } = useGetAlphaId({
        service: INCIDENT_SERVICE_PLACE,
    });
    const { currentUser } = useGetCurrentUserV2();

    const { firstMetadataAvatarType: metadataAvatarType, isLoading: isLoadingMetadataAvatarType } =
        useGetMetadataAvatarTypes({
            filters: {
                alphaId: PLACES_METADATA_AVATAR_TYPE_NAME,
            },
        });
    const { avatar, isLoading: isLoadingAvatar } = useGetAvatar({
        avatarUriId: composeUri(AVATARS, siteId),
    });
    const {
        groupAvatars,
        firstGroupAvatarId,
        isLoading: isLoadingGroup,
    } = useGetGroupAvatars({
        filters: {
            alphaId: `${GROUP_AVATAR_SITE_PREFIX}${siteId}`,
        },
    });

    const handleSubmit = ({ name, demo }: any) => {
        let dataToSend = omitBy(
            {
                parent: avatar?.id,
                name: name,
                mcs: [],
                generateMCFinger: composeUriId(FINGERS, FINGER_ID),
                generateMCQuantity: 1,
                properties: [],
                metadataAvatarType: metadataAvatarType?.id,
                groupAvatars: groupAvatars && groupAvatars.length > 0 ? [firstGroupAvatarId ?? ''] : [],
                company: currentUser?.company,
                customFields: {
                    demo: !!demo,
                },
            },
            isNil,
        );

        getAlphaId().then((data: { counter: number; identifier: string }) => {
            create(
                `${PLACES}_${siteId}`,
                {
                    data: {
                        ...dataToSend,
                        alphaId: `P:${data.counter}`,
                    },
                    meta: {
                        api: 'v2',
                        realResource: AVATARS,
                    },
                },
                {
                    onSuccess: () => {
                        notify('ra.notification.created', { type: 'success' });
                        onClose();
                    },
                },
            );
        });
    };

    return (
        <DialogTemplate
            open={open}
            onClose={onClose}
            icon={<AddHomeOutlined />}
            title={translate('place.create_place_label', {
                siteName: avatar?.name,
            })}
            closeButtonDisabled={isLoadingCreate}
        >
            <Form noValidate onSubmit={handleSubmit}>
                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                    }}
                >
                    <FormInputsSeparator>
                        <TextInput
                            helperText={false}
                            name={'name'}
                            source={'name'}
                            label={'place.form.place_name'}
                            validate={required()}
                            autoFocus
                        />
                        {permissions?.includes(ROLE_BIG_DEMO) && (
                            <BooleanInput
                                label='place.form.demo'
                                source='demo'
                                helperText={false}
                                sx={{ ml: 1 }}
                            />
                        )}
                    </FormInputsSeparator>

                    <ButtonsBlock>
                        <Button onClick={onClose} disabled={isLoadingCreate}>
                            {translate('ra.action.back')}
                        </Button>

                        <LoadingButton
                            loading={
                                isLoadingCreate ||
                                isLoadingMetadataAvatarType ||
                                isLoadingAvatar ||
                                isLoadingGetAlphaId ||
                                isLoadingGroup
                            }
                            type={'submit'}
                            variant={'contained'}
                        >
                            {translate('ra.action.create')}
                        </LoadingButton>
                    </ButtonsBlock>
                </Box>
            </Form>
        </DialogTemplate>
    );
};

export default CreatePlaceDialog;
